import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  TablePagination,
  Divider,
  Pagination,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  SwapHoriz as TransferIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { formatCurrency } from '../utils/currency';
import { useGetInventoriesQuery, useAdjustInventoryMutation, useTransferInventoryMutation, useGetInventoryAdjustmentHistoryQuery } from '../services/inventory.service';
import { useGetLocationsQuery } from '../services/locations.service';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Role } from '../types';

interface InventoryAdjustment {
  productId: string;
  locationId: string;
  type: 'SET' | 'ADD' | 'SUBTRACT';
  quantity: number;
  reason: string;
}

interface InventoryTransfer {
  productId: string;
  fromLocationId: string;
  toLocationId: string;
  quantity: number;
  reason?: string;
}

const categories = [
  'CONTENEDORES',
  'PLATOS',
  'VASOS',
  'CUBIERTOS',
  'BOLSAS',
  'LIMPIEZA',
  'OTROS'
];

const InventoryPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);

  // Get current user from Redux store
  const { user } = useSelector((state: RootState) => state.auth);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [stockFilter, setStockFilter] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only search if term is at least 2 characters or empty (for clearing)
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        setDebouncedSearchTerm(searchTerm);
      }
    }, 400); // 400ms delay - faster than before but still prevents excessive calls

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedCategory, stockFilter, selectedLocation, showLowStock]);
  const [openAdjustDialog, setOpenAdjustDialog] = useState(false);
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyStartDate, setHistoryStartDate] = useState('');
  const [historyEndDate, setHistoryEndDate] = useState('');
  const [historyLocationFilter, setHistoryLocationFilter] = useState('');
  const [selectedInventory, setSelectedInventory] = useState<any>(null);

  const { control, handleSubmit, reset } = useForm<InventoryAdjustment>();
  const { control: transferControl, handleSubmit: handleTransferSubmit, reset: resetTransfer } = useForm<InventoryTransfer>();
  const [adjustInventory] = useAdjustInventoryMutation();
  const [transferInventory] = useTransferInventoryMutation();
  
  // Fetch locations for filtering
  const { data: locationsResponse } = useGetLocationsQuery({ active: true });
  const locations = locationsResponse?.data || [];

  // Fetch adjustment history
  const historyFilters = {
    page: historyPage,
    limit: 20,
    ...(historyStartDate && { startDate: historyStartDate }),
    ...(historyEndDate && { endDate: historyEndDate }),
    ...(historyLocationFilter && { locationId: historyLocationFilter }),
  };
  
  const { data: historyResponse, isLoading: isLoadingHistory } = useGetInventoryAdjustmentHistoryQuery(
    historyFilters,
    {
      skip: !openHistoryDialog,
    }
  );

  // Fetch inventory data from API with pagination
  const {
    data: inventoryResponse,
    isLoading,
    error,
    refetch
  } = useGetInventoriesQuery({
    page: currentPage,
    limit: itemsPerPage,
    search: debouncedSearchTerm || undefined,
    locationId: selectedLocation || undefined,
    lowStock: showLowStock || undefined,
  });

  const inventories = inventoryResponse?.data?.inventory || [];
  const totalCount = inventoryResponse?.pagination?.total || 0;
  const statistics = inventoryResponse?.data?.statistics;

  // Client-side filtering for stock status, category, and location
  const filteredInventories = inventories.filter(item => {
    const totalStock = item.totalQuantity || 0;
    const minStock = item.product?.minStock || 0;
    const productCategory = item.product?.category || '';
    
    // Category filter
    if (selectedCategory && productCategory !== selectedCategory) {
      return false;
    }
    
    // Location filter
    if (selectedLocation) {
      const hasLocationStock = item.locations?.some((loc: any) => 
        loc.locationId === selectedLocation && loc.quantity > 0
      );
      if (!hasLocationStock) {
        return false;
      }
    }
    
    // Stock status filter
    if (stockFilter === 'out') {
      return totalStock === 0;
    } else if (stockFilter === 'low') {
      return totalStock > 0 && totalStock <= minStock;
    } else if (stockFilter === 'available') {
      return totalStock > minStock;
    }
    
    return true; // 'all' - show everything
  });

  const handleOpenAdjustDialog = (item: any) => {
    setSelectedInventory(item);
    setOpenAdjustDialog(true);
  };

  const handleCloseAdjustDialog = () => {
    setOpenAdjustDialog(false);
    setSelectedInventory(null);
    reset();
  };

  const handleOpenTransferDialog = (item: any) => {
    setSelectedInventory(item);
    setOpenTransferDialog(true);
  };

  const handleCloseTransferDialog = () => {
    setOpenTransferDialog(false);
    setSelectedInventory(null);
    resetTransfer();
  };

  const onSubmit = async (data: InventoryAdjustment) => {
    if (!selectedInventory) return;
    
    try {
      const adjustmentData = {
        productId: selectedInventory.productId,
        locationId: data.locationId,
        type: data.type,
        quantity: Number(data.quantity), // Ensure it's a number
        reason: data.reason || 'Ajuste manual desde inventario'
      };
      
      console.log('Sending adjustment:', adjustmentData); // Debug log
      
      await adjustInventory(adjustmentData).unwrap();
      
      handleCloseAdjustDialog();
      refetch(); // Refresh the data
      alert('Inventario ajustado exitosamente');
    } catch (error: any) {
      console.error('Error adjusting inventory:', error);
      const errorMessage = error?.data?.message || 'Error al ajustar el inventario. Por favor, intente de nuevo.';
      alert(errorMessage);
    }
  };

  const onTransferSubmit = async (data: InventoryTransfer) => {
    if (!selectedInventory) return;
    
    try {
      const transferData = {
        productId: selectedInventory.productId,
        fromLocationId: data.fromLocationId,
        toLocationId: data.toLocationId,
        quantity: Number(data.quantity),
        reason: data.reason || 'Transferencia desde inventario'
      };
      
      console.log('Sending transfer:', transferData);
      
      const result = await transferInventory(transferData).unwrap();
      
      handleCloseTransferDialog();
      refetch();
      alert(result.message || 'Transferencia completada exitosamente');
    } catch (error: any) {
      console.error('Error transferring inventory:', error);
      const errorMessage = error?.data?.message || 'Error al transferir inventario. Por favor, intente de nuevo.';
      alert(errorMessage);
    }
  };

  const getStockStatus = (quantity: number, minStock: number) => {
    if (quantity < 0) {
      return { label: `Negativo (${quantity})`, color: 'error' as const };
    } else if (quantity === 0) {
      return { label: 'Sin Stock', color: 'error' as const };
    } else if (quantity <= minStock) {
      return { label: 'Stock Bajo', color: 'warning' as const };
    } else {
      return { label: 'Disponible', color: 'success' as const };
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar el inventario. {'data' in error ? String(error.data) : 'Error desconocido'}
        </Alert>
        <Button variant="contained" onClick={() => refetch()}>
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Inventario
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => {
              setOpenHistoryDialog(true);
              setHistoryPage(1);
              setHistoryStartDate('');
              setHistoryEndDate('');
              setHistoryLocationFilter('');
            }}
          >
            Historial de Ajustes
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => console.log('Importar inventario')}
          >
            Importar
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3, flexShrink: 0 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, SKU, código o código alternativo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              helperText={searchTerm && !debouncedSearchTerm && searchTerm.length >= 2 ? 'Buscando...' : searchTerm.length === 1 ? 'Escribe al menos 2 caracteres' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid size={{ xs: 6, sm: 4, md: 1.5 }}>
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Categoría"
              >
                <MenuItem value="">Todas</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 1.5 }}>
            <FormControl fullWidth>
              <InputLabel>Tienda</InputLabel>
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                label="Tienda"
              >
                <MenuItem value="">Todas</MenuItem>
                {locations.map(loc => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 6, sm: 4, md: 1.5 }}>
            <FormControl fullWidth>
              <InputLabel>Stock</InputLabel>
              <Select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                label="Stock"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="available">Disponible</MenuItem>
                <MenuItem value="low">Bajo</MenuItem>
                <MenuItem value="out">Agotado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 6, sm: 4, md: 1.5 }}>
            <Button
              fullWidth
              variant={showLowStock ? 'contained' : 'outlined'}
              startIcon={<WarningIcon />}
              onClick={() => setShowLowStock(!showLowStock)}
              color="warning"
            >
              Stock Bajo
            </Button>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('');
                setStockFilter('all');
                setShowLowStock(false);
                setSelectedLocation('');
                setCurrentPage(1);
              }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {filteredInventories.length > 0 ? `Mostrando ${filteredInventories.length} registros${inventories.length !== filteredInventories.length ? ` de ${inventories.length} filtrados` : ''} de ${totalCount} total` : totalCount > 0 ? 'Use filtros para refinar los resultados' : 'No hay productos en el inventario'}
            </Typography>
            {statistics && (
              <Typography variant="body2" color="text.secondary">
                📦 {statistics.totalProducts} productos • 📊 {statistics.totalStock} unidades totales • 💰 {formatCurrency(statistics.totalValue)}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            💡 La tabla es desplazable verticalmente
          </Typography>
        </Box>
      </Paper>

      <TableContainer 
        component={Paper} 
        sx={{ 
          flexGrow: 1,
          overflow: 'auto',
          maxHeight: 'calc(100vh - 300px)'
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Producto</TableCell>
              <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>SKU</TableCell>
              <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Código de Barras</TableCell>
              <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Categoría</TableCell>
              <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Ubicación</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Cantidad</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Stock Mín.</TableCell>
              <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Estado</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Valor Total</TableCell>
              <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Última Act.</TableCell>
              <TableCell align="center" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInventories.map((item, index) => {
              const minStock = item.product?.minStock || 0;
              const status = getStockStatus(item.totalQuantity || 0, minStock);
              const totalValue = item.totalValue || 0;
              
              return (
                <TableRow 
                  key={item.productId || index}
                  hover
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {item.product?.name || 'Producto desconocido'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {item.product?.sku || item.product?.code || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {item.product?.barcode || 'Sin código'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={item.product?.category || 'Sin categoría'} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        item.locations && item.locations.length > 0 ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Distribución de Inventario:
                            </Typography>
                            {item.locations.map((loc: any) => (
                              <Box key={loc.locationId} sx={{ mb: 0.5 }}>
                                <Typography variant="body2">
                                  {loc.locationName}: {loc.quantity} {item.product?.unit || 'unidades'}
                                  {loc.reserved > 0 && ` (${loc.reserved} reservadas)`}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : 'Sin información de ubicación'
                      }
                      placement="right"
                      arrow
                    >
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textDecorationStyle: 'dotted'
                        }}
                      >
                        {item.locationsCount > 1 ? `${item.locationsCount} ubicaciones` : item.locations?.[0]?.locationName || 'Ubicación desconocida'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={status.color === 'error' ? 'error' : 'inherit'}
                    >
                      {(item.totalQuantity || 0).toLocaleString()} {item.product?.unit || ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {(minStock || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={status.label}
                      color={status.color}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(totalValue)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {item.locations?.[0]?.lastUpdated ? new Date(item.locations[0].lastUpdated).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenAdjustDialog(item)}
                        variant="outlined"
                      >
                        Ajustar
                      </Button>
                      <Button
                        size="small"
                        startIcon={<TransferIcon />}
                        onClick={() => handleOpenTransferDialog(item)}
                        variant="outlined"
                        color="secondary"
                      >
                        Transferir
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modern Pagination */}
      {inventoryResponse?.pagination && inventoryResponse.pagination.totalPages > 1 && (
        <Paper sx={{ p: 3, mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Página {inventoryResponse.pagination.page} de {inventoryResponse.pagination.totalPages} 
              ({inventoryResponse.pagination.total} productos)
            </Typography>
            <Pagination
              count={inventoryResponse.pagination.totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Por página</InputLabel>
              <Select
                value={itemsPerPage}
                label="Por página"
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>
      )}

      {filteredInventories.length === 0 && !isLoading && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <Typography variant="h6" color="text.secondary">
            {!debouncedSearchTerm && !showLowStock && !selectedCategory && stockFilter === 'all' && !selectedLocation
              ? 'No hay productos en el inventario'
              : 'No se encontraron registros'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {!debouncedSearchTerm && !showLowStock && !selectedCategory && stockFilter === 'all' && !selectedLocation
              ? 'No se encontraron productos en la base de datos'
              : 'Intenta con otros filtros de búsqueda'}
          </Typography>
        </Paper>
      )}

      {/* Dialog de ajuste de inventario */}
      <Dialog open={openAdjustDialog} onClose={handleCloseAdjustDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            Ajustar Inventario
          </DialogTitle>
          
          <DialogContent>
            {selectedInventory && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">{selectedInventory.product?.name || 'Producto'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Stock total: {selectedInventory.totalQuantity || 0} {selectedInventory.product?.unit || 'unidades'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ubicaciones: {selectedInventory.locationsCount || 0} ubicaciones con stock
                </Typography>
              </Box>
            )}
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth required>
                  <InputLabel>Ubicación</InputLabel>
                  <Controller
                    name="locationId"
                    control={control}
                    rules={{ required: 'La ubicación es requerida' }}
                    render={({ field }) => (
                      <Select {...field} label="Ubicación">
                        {selectedInventory?.locations?.map((loc: any) => (
                          <MenuItem key={loc.locationId} value={loc.locationId}>
                            {loc.locationName} (Stock actual: {loc.quantity})
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Ajuste</InputLabel>
                  <Controller
                    name="type"
                    control={control}
                    defaultValue="SET"
                    render={({ field }) => (
                      <Select {...field} label="Tipo de Ajuste">
                        <MenuItem value="SET">Establecer Cantidad</MenuItem>
                        <MenuItem value="ADD">Agregar Stock</MenuItem>
                        {user?.role !== Role.CASHIER && (
                          <MenuItem value="SUBTRACT">Reducir Stock</MenuItem>
                        )}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="quantity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Cantidad"
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Razón del Ajuste"
                      multiline
                      rows={3}
                      placeholder="Describe la razón del ajuste de inventario..."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseAdjustDialog}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained">
              Confirmar Ajuste
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de transferencia de inventario */}
      <Dialog open={openTransferDialog} onClose={handleCloseTransferDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleTransferSubmit(onTransferSubmit)}>
          <DialogTitle>
            Transferir Inventario
          </DialogTitle>
          
          <DialogContent>
            {selectedInventory && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">{selectedInventory.product?.name || 'Producto'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Stock total: {selectedInventory.totalQuantity || 0} {selectedInventory.product?.unit || 'unidades'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Distribución: {selectedInventory.locationsCount || 0} ubicaciones con stock
                </Typography>
              </Box>
            )}
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth required>
                  <InputLabel>Ubicación Origen</InputLabel>
                  <Controller
                    name="fromLocationId"
                    control={transferControl}
                    rules={{ required: 'La ubicación origen es requerida' }}
                    render={({ field }) => (
                      <Select {...field} label="Ubicación Origen">
                        {selectedInventory?.locations
                          ?.filter((loc: any) => loc.quantity > 0)
                          ?.map((loc: any) => (
                          <MenuItem key={loc.locationId} value={loc.locationId}>
                            {loc.locationName} (Stock: {loc.quantity})
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth required>
                  <InputLabel>Ubicación Destino</InputLabel>
                  <Controller
                    name="toLocationId"
                    control={transferControl}
                    rules={{ required: 'La ubicación destino es requerida' }}
                    render={({ field }) => (
                      <Select {...field} label="Ubicación Destino">
                        {locations.map((loc) => (
                          <MenuItem key={loc.id} value={loc.id}>
                            {loc.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="quantity"
                  control={transferControl}
                  rules={{ 
                    required: 'La cantidad es requerida',
                    min: { value: 1, message: 'La cantidad debe ser mayor a 0' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Cantidad a Transferir"
                      type="number"
                      inputProps={{ min: 1 }}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="reason"
                  control={transferControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Razón de la Transferencia"
                      multiline
                      rows={3}
                      placeholder="Describe la razón de la transferencia..."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseTransferDialog}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="secondary">
              Confirmar Transferencia
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={openHistoryDialog} onClose={() => setOpenHistoryDialog(false)} maxWidth="xl" fullWidth>
        <DialogTitle>Historial de Ajustes de Inventario</DialogTitle>
        <DialogContent>
          {/* Filters */}
          <Box sx={{ mb: 3, pt: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  value={historyStartDate}
                  onChange={(e) => setHistoryStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={historyEndDate}
                  onChange={(e) => setHistoryEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ubicación</InputLabel>
                  <Select
                    value={historyLocationFilter}
                    onChange={(e) => setHistoryLocationFilter(e.target.value)}
                    label="Ubicación"
                  >
                    <MenuItem value="">Todas las ubicaciones</MenuItem>
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setHistoryStartDate('');
                    setHistoryEndDate('');
                    setHistoryLocationFilter('');
                    setHistoryPage(1);
                  }}
                  size="small"
                  fullWidth
                >
                  Limpiar filtros
                </Button>
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <Box sx={{ mt: 2 }}>
            {isLoadingHistory ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell>Ubicación</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Cantidad Anterior</TableCell>
                    <TableCell>Cantidad Nueva</TableCell>
                    <TableCell>Ajuste</TableCell>
                    <TableCell>Motivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyResponse?.data?.map((adjustment) => (
                    <TableRow key={adjustment.id}>
                      <TableCell>
                        {new Date(adjustment.date).toLocaleString('es-ES')}
                      </TableCell>
                      <TableCell>{adjustment.user.name}</TableCell>
                      <TableCell>{adjustment.details.productName}</TableCell>
                      <TableCell>{adjustment.location?.name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={adjustment.details.type}
                          color={
                            adjustment.details.type === 'ADD' ? 'success' : 
                            adjustment.details.type === 'SUBTRACT' ? 'error' : 
                            'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{adjustment.details.previousQuantity}</TableCell>
                      <TableCell>{adjustment.details.newQuantity}</TableCell>
                      <TableCell>
                        {adjustment.details.type === 'SET' ? adjustment.details.quantity : 
                         adjustment.details.type === 'ADD' ? `+${adjustment.details.quantity}` : 
                         `-${adjustment.details.quantity}`}
                      </TableCell>
                      <TableCell>{adjustment.details.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </TableContainer>
            )}
            {historyResponse?.data?.length === 0 && !isLoadingHistory && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  No hay ajustes registrados
                </Typography>
              </Box>
            )}
            
            {/* Pagination */}
            {historyResponse?.pagination && (
              <TablePagination
              component="div"
              count={historyResponse.pagination.total}
              page={historyPage - 1}
              onPageChange={(event, newPage) => setHistoryPage(newPage + 1)}
              rowsPerPage={20}
              onRowsPerPageChange={() => {}}
              rowsPerPageOptions={[20]}
              labelRowsPerPage="Registros por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistoryDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default InventoryPage;